import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plane, MoveUp, Loader2 } from "lucide-react";

export const CAPTURE_METHODS = [
  { value: "drone", label: "Drone Flight", hint: "Drone videos & aerials required", Icon: Plane },
  { value: "climb", label: "Tower Climbing", hint: "Climb videos & stills required", Icon: MoveUp },
];

export const captureMethodLabel = (v) => CAPTURE_METHODS.find((m) => m.value === v)?.label || "";

// Two-button choice: exactly one method per Pano site audit.
export function CaptureMethodPicker({ value, onChange, disabled = false, testid = "capture-method" }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" role="radiogroup" data-testid={testid}>
      {CAPTURE_METHODS.map(({ value: v, label, hint, Icon }) => {
        const active = value === v;
        return (
          <button
            key={v}
            type="button"
            role="radio"
            aria-checked={active}
            disabled={disabled}
            onClick={() => onChange(v)}
            className={`flex items-center gap-3 p-3 rounded-md border text-left transition-colors disabled:opacity-60 ${
              active ? "bg-blue-950/40 border-blue-500 ring-1 ring-blue-500/50" : "bg-slate-950 border-slate-700 hover:border-slate-500"
            }`}
            data-testid={`${testid}-${v}`}
          >
            <Icon className={`w-5 h-5 shrink-0 ${active ? "text-blue-300" : "text-slate-500"}`} />
            <span>
              <span className="block font-display font-bold text-sm">{label}</span>
              <span className="block text-[11px] text-slate-400">{hint}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

// Project header row: shows the method; office staff can switch it.
export function CaptureMethodRow({ project, canEdit, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(!project.capture_method && canEdit);
  const save = async (v) => {
    if (v === project.capture_method) return setEditing(false);
    setSaving(true);
    try {
      await api.patch(`/projects/${project.id}`, { capture_method: v });
      toast.success(`Method set to ${captureMethodLabel(v)}`);
      setEditing(false);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update method");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="mt-2 text-xs" data-testid="capture-method-row">
      <div className="flex items-center gap-2 text-slate-400">
        <span className="uppercase tracking-wider text-[10px]">Method</span>
        <span className={project.capture_method ? "text-blue-300 font-semibold" : "text-amber-300"} data-testid="capture-method-value">
          {captureMethodLabel(project.capture_method) || "Not chosen — both sections optional"}
        </span>
        {saving && <Loader2 className="w-3 h-3 animate-spin" />}
        {canEdit && !editing && (
          <button type="button" onClick={() => setEditing(true)} className="text-blue-400 hover:text-blue-200 underline" data-testid="capture-method-change">
            Change
          </button>
        )}
        {editing && project.capture_method && (
          <button type="button" onClick={() => setEditing(false)} className="text-slate-400 hover:text-slate-200 underline" data-testid="capture-method-cancel">
            Cancel
          </button>
        )}
      </div>
      {editing && (
        <div className="mt-2 max-w-md">
          <CaptureMethodPicker value={project.capture_method} onChange={save} disabled={saving} testid="capture-method-edit" />
        </div>
      )}
    </div>
  );
}
